/**
 * Local-only player profile. Stored in localStorage on the device — no
 * server round-trip required for any student data. Multiplayer, classes,
 * PINs and the teacher dashboard are all bypassed in this mode.
 *
 * The lesson catalog still comes from the server (it's static, global
 * content that doesn't need auth) but the profile owns everything else:
 * name, avatar, coin balance, purchased upgrades, lesson progress and
 * per-word SRS mastery.
 */
import type { AvatarConfig } from "@shared/types";
import type { UpgradeId, UpgradeLevels } from "@shared/upgrades";
import { UPGRADES } from "@shared/upgrades";
import { nextSrsState } from "@shared/game";
import { newlyUnlocked, unlockedIdsFor, type UnlockItem } from "./unlocks";

export interface LocalProgressRow {
  runs: number;
  bestScore: number;
  bestTimeMs: number | null;
  lastPlayedAt: number | null;
  /** Set the first time the player actually crosses the finish line. Drives
   *  level-unlock logic: lesson N+1 is locked until lesson N is finished. */
  firstFinishedAt: number | null;
}

export interface LocalMasteryRow {
  timesSeen: number;
  timesCorrect: number;
  streak: number;
  srsLevel: number;
  srsDueAt: number | null;
  lastSeenAt: number | null;
}

export interface LocalProfile {
  /** Local-only id; never sent to the server. */
  id: string;
  displayName: string;
  avatarConfig: AvatarConfig;
  coins: number;
  upgrades: UpgradeLevels;
  /** keyed by lessonId */
  progress: Record<string, LocalProgressRow>;
  /** keyed by wordId */
  mastery: Record<string, LocalMasteryRow>;
  /** Total number of runs completed (any outcome). Drives cosmetic unlocks. */
  lifetimeRuns: number;
  /** Cosmetic unlock ids the player has earned. */
  unlocked: string[];
  /** 4-digit cross-device account code, if signed in via /api/player/*. */
  code?: string;
  /** Which language the player is *learning*. "en" = English (default; the
   *  prompts speak English and ask for English answers). "vi" = Vietnamese
   *  (the prompts speak Vietnamese and ask for Vietnamese answers; English
   *  is shown as the helper). Set on character creation. */
  learnLanguage?: "en" | "vi";
}

const STORAGE_KEY = "wr_profile_v1";

function makeId(): string {
  const r = new Uint8Array(8);
  crypto.getRandomValues(r);
  let out = "p_";
  for (const b of r) out += b.toString(16).padStart(2, "0");
  return out;
}

export function loadProfile(): LocalProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as LocalProfile;
    // Guard against missing fields if the shape evolves.
    if (!p.id || !p.displayName) return null;
    p.coins = p.coins ?? 0;
    p.upgrades = p.upgrades ?? {};
    p.progress = p.progress ?? {};
    p.mastery = p.mastery ?? {};
    p.avatarConfig = p.avatarConfig ?? {};
    p.lifetimeRuns = p.lifetimeRuns ?? Object.values(p.progress).reduce((a, r) => a + (r.runs ?? 0), 0);
    // Always recompute from the current catalog so new unlocks added in
    // an update (or the EVERYTHING_UNLOCKED dev flag) appear without
    // forcing the player to reset their profile.
    p.unlocked = unlockedIdsFor(p.lifetimeRuns);
    return p;
  } catch {
    return null;
  }
}

export function saveProfile(p: LocalProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* quota or private mode — ignore */
  }
}

export function clearProfile(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function createProfile(
  displayName: string,
  avatarConfig: AvatarConfig,
  learnLanguage: "en" | "vi" = "en",
): LocalProfile {
  const p: LocalProfile = {
    id: makeId(),
    displayName: displayName.trim(),
    avatarConfig,
    coins: 0,
    upgrades: {},
    progress: {},
    mastery: {},
    lifetimeRuns: 0,
    unlocked: unlockedIdsFor(0),
    learnLanguage,
  };
  saveProfile(p);
  return p;
}

export function updateAvatar(p: LocalProfile, avatar: AvatarConfig): LocalProfile {
  const next = { ...p, avatarConfig: { ...p.avatarConfig, ...avatar } };
  saveProfile(next);
  return next;
}

export function updateName(p: LocalProfile, name: string): LocalProfile {
  const next = { ...p, displayName: name.trim() };
  saveProfile(next);
  return next;
}

export interface RunOutcomeForProfile {
  lessonId: string;
  score: number;
  timeMs: number;
  coinsEarned: number;
  hearts: number;
  wordOutcomes: { wordId: string; correct: boolean }[];
  /** Did the runner actually cross the finish line (vs. died en route). */
  finished: boolean;
}

export interface RecordRunResult {
  profile: LocalProfile;
  newlyUnlocked: UnlockItem[];
}

export function recordRun(p: LocalProfile, o: RunOutcomeForProfile): RecordRunResult {
  const now = Date.now();
  const priorRuns = p.lifetimeRuns ?? 0;
  const nextRuns = priorRuns + 1;
  const awarded = newlyUnlocked(p.unlocked ?? [], priorRuns, nextRuns);
  const next: LocalProfile = {
    ...p,
    coins: p.coins + o.coinsEarned,
    progress: { ...p.progress },
    mastery: { ...p.mastery },
    lifetimeRuns: nextRuns,
    unlocked: awarded.length > 0
      ? [...(p.unlocked ?? []), ...awarded.map((u) => u.id)]
      : (p.unlocked ?? []),
  };

  // Progress row. Only stamp firstFinishedAt on the very first finish so it
  // serves as a reliable "this lesson is unlocked downstream" signal.
  const prev = next.progress[o.lessonId];
  next.progress[o.lessonId] = prev
    ? {
        runs: prev.runs + 1,
        bestScore: Math.max(prev.bestScore, o.score),
        bestTimeMs:
          prev.bestTimeMs == null ? o.timeMs : Math.min(prev.bestTimeMs, o.timeMs),
        lastPlayedAt: now,
        firstFinishedAt: prev.firstFinishedAt ?? (o.finished ? now : null),
      }
    : {
        runs: 1,
        bestScore: o.score,
        bestTimeMs: o.timeMs,
        lastPlayedAt: now,
        firstFinishedAt: o.finished ? now : null,
      };

  // Per-word mastery + SRS.
  for (const wo of o.wordOutcomes) {
    const m = next.mastery[wo.wordId];
    const prevLevel = m?.srsLevel ?? 0;
    const ns = nextSrsState({ srsLevel: prevLevel, correct: wo.correct, now });
    const streak = wo.correct ? (m?.streak ?? 0) + 1 : 0;
    next.mastery[wo.wordId] = {
      timesSeen: (m?.timesSeen ?? 0) + 1,
      timesCorrect: (m?.timesCorrect ?? 0) + (wo.correct ? 1 : 0),
      streak,
      srsLevel: ns.srsLevel,
      srsDueAt: ns.srsDueAt,
      lastSeenAt: now,
    };
  }

  saveProfile(next);
  return { profile: next, newlyUnlocked: awarded };
}

/** Has the lesson been finished at least once? */
export function isLessonFinished(p: LocalProfile, lessonId: string): boolean {
  return !!p.progress[lessonId]?.firstFinishedAt;
}

/**
 * Lessons 1-3 are always unlocked (intros). Level N (N >= 4) unlocks once
 * the player has finished level N-1 at least once. `lessonsByOrder` is the
 * sorted list of all lessons.
 */
export function isLessonUnlocked(
  _p: LocalProfile | null,
  _orderIndex: number,
  _lessonsByOrder: { id: string; orderIndex: number }[],
): boolean {
  // All levels are unlocked from the start — players can pick whichever they
  // want. The old "finish level N-1 first" gate has been removed.
  return true;
}

/**
 * Try to buy one level of an upgrade. Returns the updated profile, or null
 * if the upgrade is maxed or coins are insufficient.
 */
export function buyUpgrade(p: LocalProfile, id: UpgradeId): LocalProfile | null {
  const def = UPGRADES.find((u) => u.id === id);
  if (!def) return null;
  const cur = p.upgrades[id] ?? 0;
  if (cur >= def.maxLevel) return null;
  const cost = def.costFor(cur);
  if (p.coins < cost) return null;
  const next: LocalProfile = {
    ...p,
    coins: p.coins - cost,
    upgrades: { ...p.upgrades, [id]: cur + 1 },
  };
  saveProfile(next);
  return next;
}
